// Message.jsx

import React, { useEffect, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { formatChannelRelativeTime } from 'channels/utils/channelTime';
import { resolveThreadSummary } from 'channels/utils/threadSummaries';
import { PhotoShape } from 'utilities/Enums';

// CSS
import './Message.css';

// Components
import MessageActions from './message/MessageActions';
import MessageAttachments from './message/MessageAttachments';
import MessageEditor from './message/MessageEditor';
import MessageHeader from './message/MessageHeader';
import MessageThreadAffordance from './message/MessageThreadAffordance';
import MessageText from 'channels/components/MessageText';
import ProfilePhoto from 'components/profilephoto/ProfilePhoto';

/**
 * Message
 *
 * Renders one chat message row with inline edit/delete actions.
 *
 * @param {object} message - Message row metadata.
 * @param {boolean} isOwn - Whether the message belongs to the active user.
 * @param {object} attachmentNodesByKey - Hydrated node references keyed by node key.
 * @param {function} onOpenAttachmentNode - Called when shared attachment is selected.
 * @param {function} onEdit - Called when saving an edit.
 * @param {function} onDelete - Called when deleting a message.
 * @param {boolean} showAvatarConnector - Whether to render a timeline line below the avatar.
 * @param {boolean} isHighlighted - Whether to flash a temporary highlight.
 *
 * @returns {ReactNode} The rendered message row.
 */
const Message = React.memo(({
  message,
  isOwn,
  attachmentNodesByKey,
  onOpenAttachmentNode,
  onEdit,
  onDelete,
  showAvatarConnector = false,
  isHighlighted = false,
  isRootMessage = false,
  canReplyInThread = false,
  hasUnreadReplies = false,
  onOpenThread,
  onReplyInThread,
  membersByKey = {},
  mentionedPeopleByKey = {},
  allowEditDelete = true,
  allowDeleteAny = false,
  threadSummariesByRootKey = {},
}) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();

  // ----- STATE ----- //

  const [isEditing, setIsEditing] = useState(false);
  const [draftText, setDraftText] = useState(message?.text || '');

  // ----- EFFECTS ----- //

  useEffect(() => {
    if (isEditing) return;
    setDraftText(message?.text || '');
  }, [isEditing, message?.text]);

  const avatarNode = message?.user || null;
  const displayName = avatarNode?.name || message?.userName || 'Member';

  const createdLabel = formatChannelRelativeTime(message?.dateCreated);
  const edited = Boolean(message?.isEdited);
  const deleted = Boolean(message?.dateDeleted);
  const { replyCount, lastReplyAt } = resolveThreadSummary(message, threadSummariesByRootKey);
  const showReplyInThreadAction = canReplyInThread
    && !message?.parentMessageKey
    && replyCount <= 0;

  // ----- HANDLERS ----- //

  /**
   * Handles save.
   */

  const handleSave = async () => {
    const cleanDraft = String(draftText || '').trim();
    if (!cleanDraft || cleanDraft === String(message?.text || '').trim()) {
      setIsEditing(false);
      return;
    }

    const ok = await onEdit?.({ messageKey: message?.key, text: cleanDraft });
    if (ok) {
      setIsEditing(false);
    }
  };

  /**
 * Handles cancel edit.
 */
  const handleCancelEdit = () => {
    setDraftText(message?.text || '');
    setIsEditing(false);
  };

  // ----- RENDER ----- //

  return (
    <article
      className={`message-container${isHighlighted ? ' is-highlighted' : ''}`}
      id={message?.key ? `message-${message.key}` : undefined}
    >

      {/* AVATAR COLUMN */}
      <div className="message-avatar-column">

        {/* AVATAR */}
        <div className="message-avatar">
          <ProfilePhoto
            node={avatarNode}
            size={32}
            borderRadius="8px"
            shape={PhotoShape.SQUARE}
          />
        </div>

        {/* AVATAR CONNECTOR */}
        {showAvatarConnector ? (
          <div
            className="message-avatar-connector"
            aria-hidden
          />
        ) : null}
      </div>

      {/* MAIN */}
      <div className="message-main">
        <MessageHeader
          displayName={displayName}
          createdLabel={createdLabel}
          edited={edited}
          deleted={deleted}
          theme={theme}
        />

        {/* DELETED */}
        {deleted ? (
          <div
            className="message-deleted"
          >
            Message deleted
          </div>
        ) : null}

        {!deleted && isEditing ? (
          <MessageEditor
            draftText={draftText}
            theme={theme}
            onCancel={handleCancelEdit}
            onChangeDraft={setDraftText}
            onSave={handleSave}
          />
        ) : null}

        {!deleted && !isEditing ? (
          <MessageText
            text={message?.text}
            mentionKeys={message?.mentionKeys}
            membersByKey={membersByKey}
            mentionedPeopleByKey={mentionedPeopleByKey}
            attachments={message?.attachments}
          />
        ) : null}

        {!deleted ? (
          <MessageAttachments
            attachments={message?.attachments}
            attachmentNodesByKey={attachmentNodesByKey}
            onOpenAttachmentNode={onOpenAttachmentNode}
          />
        ) : null}

        {!deleted && !isRootMessage && !message?.parentMessageKey ? (
          <MessageThreadAffordance
            replyCount={replyCount}
            lastReplyAt={lastReplyAt}
            hasUnreadReplies={hasUnreadReplies}
            onOpenThread={() => onOpenThread?.(message?.key)}
          />
        ) : null}
      </div>

      <MessageActions
        isVisible={
          !isRootMessage
          && !deleted
          && (
            isOwn
            || showReplyInThreadAction
            || (allowDeleteAny && allowEditDelete)
          )
        }
        theme={theme}
        showReplyInThread={showReplyInThreadAction}
        showEdit={isOwn && allowEditDelete}
        showDelete={allowEditDelete && (isOwn || allowDeleteAny)}
        onDelete={() => onDelete?.({ messageKey: message?.key })}
        onReplyInThread={() => onReplyInThread?.(message?.key)}
        onToggleEdit={() => setIsEditing((prev) => !prev)}
      />
    </article>
  );
});

// Dev Tools
Message.displayName = 'Message';

export default Message;
